import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../context/ThemeContext';

// Icons
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const ClearIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="m15 9-6 6" />
    <path d="m9 9 6 6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const StarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// Variant options
const VARIANTS = [
  { value: 'Normal', label: 'Normal' },
  { value: 'Holo', label: 'Holo' },
  { value: 'Reverse Holo', label: 'Reverse Holo' },
  { value: 'Full Art', label: 'Full Art' },
  { value: 'Alt Art', label: 'Alt Art' },
  { value: 'Special Art', label: 'Special Art' },
  { value: '1st Edition', label: '1st Edition' },
  { value: 'Shadowless', label: 'Shadowless' },
];

// TCGdex API helper
const getTCGdexEndpoint = (language = 'en') => {
  return `https://api.tcgdex.net/v2/${language}`;
};

// Search TCGdex for cards by name
const searchTCGDex = async (searchName, language = 'en') => {
  if (!searchName || searchName.trim().length < 2) return [];

  try {
    const apiEndpoint = getTCGdexEndpoint(language);
    const response = await fetch(
      `${apiEndpoint}/cards?name=${encodeURIComponent(searchName.trim())}`
    );
    if (response.ok) {
      return await response.json();
    }
  } catch (e) {
    console.error('TCGdex search error:', e);
  }
  return [];
};

// Load card details with images
const loadCardDetails = async (cards, startIndex = 0, batchSize = 20, language = 'en') => {
  const batch = cards.slice(startIndex, startIndex + batchSize);
  const apiEndpoint = getTCGdexEndpoint(language);

  const detailedCards = await Promise.all(
    batch.map(async (card) => {
      try {
        const detailRes = await fetch(`${apiEndpoint}/cards/${card.id}`);
        if (detailRes.ok) {
          const details = await detailRes.json();
          return {
            id: details.id,
            name: details.name,
            setId: details.set?.id || '',
            setName: details.set?.name || 'Unknown Set',
            cardNumber: details.localId,
            imageUrl: details.image ? `${details.image}/high.webp` : null,
            rarity: details.rarity,
          };
        }
      } catch (e) {
        console.error('Error fetching card details:', e);
      }
      return null;
    })
  );

  return detailedCards.filter(Boolean);
};

const WishListSearchModal = ({ isOpen, onClose, userId, wishListCount, priorityCount, onSuccess }) => {
  const { theme, isDark } = useTheme();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState('Normal');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Clear state when closed
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setSelectedCard(null);
      setSelectedVariant('Normal');
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  // Search function
  const searchCards = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cards = await searchTCGDex(searchQuery);
      if (cards.length > 0) {
        const detailed = await loadCardDetails(cards, 0, 30);
        setResults(detailed);
      } else {
        setResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchCards(query);
    }, 400);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchCards]);

  // Handle adding card to wish list
  const handleAddToWishList = async () => {
    if (!selectedCard || !userId) return;

    setAdding(true);
    setError(null);

    // Determine if this card should be priority (first 20 are auto-priority)
    const currentPriorityCount = priorityCount ?? 0;
    const willBePriority = currentPriorityCount < 20;

    try {
      const { error: insertError } = await supabase
        .from('wish_list')
        .insert({
          user_id: userId,
          tcgdex_card_id: selectedCard.id,
          card_name: selectedCard.name,
          set_id: selectedCard.setId,
          set_name: selectedCard.setName,
          variant: selectedVariant,
          is_priority: willBePriority,
        });

      if (insertError) {
        if (insertError.code === '23505') {
          setError('This card + variant is already in your wish list');
        } else if (insertError.message?.includes('priority') || insertError.message?.includes('limit')) {
          setError('Top 20 is full - card added to More');
        } else {
          setError('Failed to add card');
          console.error('Insert error:', insertError);
        }
        return;
      }

      if (willBePriority) {
        setSuccess(`Added to Top 20 (${currentPriorityCount + 1}/20)`);
      } else {
        setSuccess('Added to More Wishes');
      }
      setSelectedCard(null);
      setSelectedVariant('Normal');

      // Notify parent to refresh
      if (onSuccess) onSuccess();

      // Clear success message after delay
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Add error:', err);
      setError('Failed to add card');
    } finally {
      setAdding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{...styles.overlay, backgroundColor: theme.bg}}>
      <div style={styles.container}>
        {/* Header */}
        <div style={{...styles.header, borderBottom: `1px solid ${theme.border}`}}>
          <div style={{...styles.searchInputContainer, backgroundColor: theme.bgSheet, border: `1px solid ${theme.border}`, color: theme.textMuted}}>
            <SearchIcon />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search any Pokemon card..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{...styles.searchInput, color: theme.text}}
            />
            {query && (
              <button style={{...styles.clearButton, color: theme.textSecondary}} onClick={() => setQuery('')}>
                <ClearIcon />
              </button>
            )}
          </div>
          <button style={{...styles.closeButton, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.text}} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Counter */}
        <div style={{...styles.counterBar, backgroundColor: theme.warningBg, borderBottom: `1px solid ${theme.warning}`, color: theme.warning}}>
          <StarIcon />
          <span style={styles.counterText}>Top 20: {priorityCount ?? 0}/20</span>
          {wishListCount > (priorityCount ?? 0) && (
            <span style={{...styles.counterText, color: theme.textSecondary, marginLeft: '8px'}}>
              | More: {wishListCount - (priorityCount ?? 0)}
            </span>
          )}
        </div>

        {/* Messages */}
        {error && (
          <div style={styles.errorBar}>
            {error}
          </div>
        )}
        {success && (
          <div style={styles.successBar}>
            <CheckIcon />
            {success}
          </div>
        )}

        {/* Content */}
        <div style={styles.content}>
          {loading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner} />
              <span style={{...styles.loadingText, color: theme.textSecondary}}>Searching TCGdex...</span>
            </div>
          ) : !query.trim() ? (
            <div style={{...styles.emptyState, color: theme.textSecondary}}>
              <SearchIcon />
              <span style={styles.emptyText}>Search for any Pokemon card</span>
              <span style={styles.emptySubtext}>Type at least 2 characters to search</span>
            </div>
          ) : results.length === 0 ? (
            <div style={{...styles.emptyState, color: theme.textSecondary}}>
              <span style={styles.emptyText}>No cards found</span>
              <span style={styles.emptySubtext}>Try a different search term</span>
            </div>
          ) : (
            <div style={styles.resultsGrid}>
              {results.map((card) => (
                <div
                  key={card.id}
                  style={{
                    ...styles.cardItem,
                    ...(selectedCard?.id === card.id ? styles.cardItemSelected : {}),
                  }}
                  onClick={() => {
                    setSelectedCard(card);
                    setError(null);
                  }}
                >
                  <div style={{...styles.cardImageContainer, backgroundColor: theme.bgCard}}>
                    {card.imageUrl ? (
                      <img src={card.imageUrl} alt={card.name} style={styles.cardImage} />
                    ) : (
                      <div style={styles.cardImagePlaceholder}>
                        <span>{card.name?.[0]}</span>
                      </div>
                    )}
                    {selectedCard?.id === card.id && (
                      <div style={{...styles.selectedOverlay, color: theme.text}}>
                        <CheckIcon />
                      </div>
                    )}
                  </div>
                  <span style={{...styles.cardName, color: theme.text}}>{card.name}</span>
                  <span style={{...styles.cardSet, color: theme.textSecondary}}>{card.setName}</span>
                  <span style={{...styles.cardNumber, color: theme.accent}}>#{card.cardNumber}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Variant Picker - shows when card is selected */}
        {selectedCard && (
          <div style={{...styles.variantPanel, backgroundColor: theme.bgSheet, borderTop: `1px solid ${theme.border}`}}>
            <div style={styles.variantHeader}>
              <span style={{...styles.variantTitle, color: theme.text}}>Select Variant</span>
              <span style={{...styles.variantCardName, color: theme.textSecondary}}>{selectedCard.name}</span>
            </div>
            <div style={styles.variantOptions}>
              {VARIANTS.map((variant) => (
                <button
                  key={variant.value}
                  style={{
                    ...styles.variantOption,
                    backgroundColor: selectedVariant === variant.value ? 'rgba(14, 165, 233, 0.15)' : theme.bgCard,
                    borderColor: selectedVariant === variant.value ? theme.accent : theme.border,
                    color: selectedVariant === variant.value ? theme.accent : theme.textMuted,
                  }}
                  onClick={() => setSelectedVariant(variant.value)}
                >
                  {variant.label}
                </button>
              ))}
            </div>
            <button
              style={{
                ...styles.addButton,
                backgroundColor: theme.accent,
                color: theme.text,
                ...(adding ? styles.addButtonDisabled : {}),
              }}
              onClick={handleAddToWishList}
              disabled={adding}
            >
              {adding ? 'Adding...' : 'Add to Wish List'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2000,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    paddingLeft: 'env(safe-area-inset-left)',
    paddingRight: 'env(safe-area-inset-right)',
  },
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px',
    flexShrink: 0,
  },
  searchInputContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRadius: '12px',
    padding: '12px 16px',
  },
  searchInput: {
    flex: 1,
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    fontSize: '16px',
    fontFamily: 'inherit',
  },
  clearButton: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  counterBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 16px',
  },
  counterText: {
    fontSize: '14px',
    fontWeight: '600',
  },
  errorBar: {
    padding: '10px 16px',
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    borderBottom: '1px solid rgba(239, 68, 68, 0.3)',
    color: '#ef4444',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'center',
  },
  successBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    borderBottom: '1px solid rgba(34, 197, 94, 0.3)',
    color: '#22c55e',
    fontSize: '13px',
    fontWeight: '500',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    gap: '12px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #2a2a2a',
    borderTopColor: '#0ea5e9',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    fontSize: '14px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    gap: '8px',
  },
  emptyText: {
    color: '#8a8a8a',
    fontSize: '16px',
    fontWeight: '500',
  },
  emptySubtext: {
    color: '#5a5a5a',
    fontSize: '14px',
  },
  resultsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    padding: '16px',
    paddingBottom: '200px',
  },
  cardItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    cursor: 'pointer',
    transition: 'transform 0.15s ease',
  },
  cardItemSelected: {
    transform: 'scale(0.95)',
  },
  cardImageContainer: {
    position: 'relative',
    width: '100%',
    aspectRatio: '0.714',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  cardImagePlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2a2a2a',
    color: '#5a5a5a',
    fontSize: '24px',
    fontWeight: '600',
  },
  selectedOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(14, 165, 233, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardName: {
    fontSize: '12px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardSet: {
    fontSize: '10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardNumber: {
    fontSize: '10px',
    fontWeight: '600',
  },
  variantPanel: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
    padding: '20px',
    paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
  },
  variantHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  variantTitle: {
    fontSize: '16px',
    fontWeight: '600',
  },
  variantCardName: {
    fontSize: '13px',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  variantOptions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '20px',
  },
  variantOption: {
    padding: '10px 16px',
    borderRadius: '20px',
    border: '1px solid',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  addButton: {
    width: '100%',
    padding: '16px',
    borderRadius: '12px',
    border: 'none',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  addButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

export default WishListSearchModal;
